import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { BrandImport, BrandProfile, KnowledgeSource } from '@unipostudio/shared-types';
import { brandService } from '@/shared/services/brand.service';
import { inboxAgentService } from '@/shared/services/inbox-agent.service';
import { useWorkspaceStore, type Workspace } from '@/shared/stores/workspace.store';
import { INBOX_AGENT_KEY, useAgentReadAllowed } from './useInboxAgent';
import { useSaveOccasionSettings } from './useOccasions';

/** Query key shared by every brand-profile read and mutation. */
export const BRAND_PROFILE_KEY = 'brand-profile';

const STALE_MS = 5 * 60 * 1000;

function useWsId(): string | undefined {
  return useWorkspaceStore((s) => s.currentWorkspace?.id);
}

export function useBrandProfile(): UseQueryResult<BrandProfile> {
  const wsId = useWsId();
  return useQuery({
    queryKey: [BRAND_PROFILE_KEY, wsId],
    queryFn: () => brandService.getProfile(),
    enabled: !!wsId,
    staleTime: STALE_MS,
  });
}

interface SaveContext {
  previous: BrandProfile | undefined;
}

/**
 * Saves a patch optimistically. On success the brand kit consumers (posters,
 * AI drafts), the Settings branding tab and the workspace list all refresh,
 * and the occasion settings follow the profile's country, region and industry
 * so the calendar matches what the brand says about itself.
 */
/** A save: the changed fields, and the profile as the server last confirmed it (AL-42). */
export interface SaveBrandProfileInput {
  patch: Partial<BrandProfile>;
  base?: BrandProfile;
}

export function useSaveBrandProfile(): UseMutationResult<BrandProfile, Error, SaveBrandProfileInput, SaveContext> {
  const queryClient = useQueryClient();
  const wsId = useWsId();
  const saveOccasions = useSaveOccasionSettings();
  const setCurrentWorkspace = useWorkspaceStore((s) => s.setCurrentWorkspace);
  const key = [BRAND_PROFILE_KEY, wsId];

  return useMutation<BrandProfile, Error, SaveBrandProfileInput, SaveContext>({
    mutationFn: ({ patch, base }) => brandService.saveProfile(patch, base),
    onMutate: async ({ patch }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BrandProfile>(key);
      if (previous) queryClient.setQueryData<BrandProfile>(key, { ...previous, ...patch });
      return { previous };
    },
    onError: (_error, _patch, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSuccess: (saved, { patch }) => {
      queryClient.setQueryData<BrandProfile>(key, saved);
      void queryClient.invalidateQueries({ queryKey: [BRAND_PROFILE_KEY] });
      void queryClient.invalidateQueries({ queryKey: ['branding'] });

      const touchesWorkspace = ['name', 'logoUrl', 'industry', 'timezone', 'website', 'languages'].some((f) => f in patch);
      if (touchesWorkspace) {
        const current = useWorkspaceStore.getState().currentWorkspace;
        if (current) {
          const next: Workspace = { ...current, name: saved.name || current.name };
          if (saved.logoUrl) next.logoUrl = saved.logoUrl;
          setCurrentWorkspace(next);
        }
        void queryClient.invalidateQueries({ queryKey: ['workspaces', 'me'] });
      }

      const touchesOccasions = ['country', 'region', 'industry'].some((f) => f in patch);
      if (touchesOccasions && saved.country) {
        saveOccasions.mutate({ country: saved.country, region: saved.region, industry: saved.industry });
      }
    },
  });
}

export interface BrandImportMutations {
  website: UseMutationResult<BrandImport, Error, string>;
  channel: UseMutationResult<BrandImport, Error, string>;
}

/** A04: import from a website URL or a connected channel. Nothing is applied until the person reviews it. */
export function useBrandImport(): BrandImportMutations {
  const website = useMutation<BrandImport, Error, string>({ mutationFn: (url) => brandService.importFromWebsite(url) });
  const channel = useMutation<BrandImport, Error, string>({ mutationFn: (platform) => brandService.importFromChannel(platform) });
  return { website, channel };
}

/** The knowledge base the inbox agent answers from; same cache entry as the inbox settings page. */
export function useBrandKnowledge(): UseQueryResult<KnowledgeSource[]> {
  const wsId = useWsId();
  const allowed = useAgentReadAllowed('agency');
  return useQuery({
    queryKey: [INBOX_AGENT_KEY, 'knowledge', wsId],
    queryFn: () => inboxAgentService.listSources(),
    enabled: allowed,
  });
}

export function useAddBrandKnowledge(): UseMutationResult<KnowledgeSource, Error, Pick<KnowledgeSource, 'kind' | 'title' | 'language' | 'content'>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Pick<KnowledgeSource, 'kind' | 'title' | 'language' | 'content'>) => inboxAgentService.addSource(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [INBOX_AGENT_KEY, 'knowledge'] });
    },
  });
}
